// src/pages/ContentDetail/ContentDetail.js
import { Box, Alert, alpha, Button } from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';

import FullPageCircularProgress from '../../../../components/progressBar/FullPageCircularProgress';
import DeleteDialog from '../../components/ContentCard/DeleteDialog';
import { useReadingPracticeDetails } from '../../hooks/useReadingPracticeDetails';
import DetailBreadcrumbs from './DetailBreadcrumbs';
import Header from './Header';
import SummaryCard from './SummaryCard';
import ActivityTabs from './ActivityTabs';
import ActionMenu from './ActionMenu';

const ReadingPracticeDetails = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const {
    content,
    loading,
    error,
    tabValue,
    menuAnchor,
    deleteDialogOpen,
    isFromDraftTab,
    isFromPublishedTab,
    isFromApprovedTab,
    isFromReleasedTab,
    isFromArchivedTab,
    handleTabChange,
    handleMenuOpen,
    handleMenuClose,
    handleEdit,
    handlePrint,
    handleDeleteClick,
    handleDeleteCancel,
    handleDeleteConfirm,
    publishContent,
    approveContent,
    releaseContent,
    archiveContent,
    readingCategories
  } = useReadingPracticeDetails();

  // Render loading state
  if (loading) {
    return (
      <FullPageCircularProgress />
    );
  }

  if (error) {
    return (
      <Box sx={{ mt: 3 }}>
        <Alert
          severity="error"
          sx={{
            background: alpha(theme.palette.error.main, 0.1),
            color: theme.palette.error.main,
            '& .MuiAlert-icon': {
              color: theme.palette.error.main
            }
          }}
        >
          {error}
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          sx={{ mt: 2, color: theme.palette.text.primary }}
          onClick={() => navigate('/')}
        >
          Back to Dashboard
        </Button>
      </Box>
    );
  }

  if (!content) return null;

  return (
    <Box>
      <DetailBreadcrumbs title={content.cover.title} />

      <Header
        title={content.cover.title}
        onBack={() => navigate('/')}
        onPrint={handlePrint}
        onMenuOpen={handleMenuOpen}
      />

      <SummaryCard
        content={content}
        readingCategories={readingCategories}
        isFromDraftTab={isFromDraftTab}
        isFromPublishedTab={isFromPublishedTab}
        isFromApprovedTab={isFromApprovedTab}
        isFromReleasedTab={isFromReleasedTab}
        isFromArchivedTab={isFromArchivedTab}
        onPublish={publishContent}
        onApprove={approveContent}
        onRelease={releaseContent}
        onArchive={archiveContent}
      />

      <ActivityTabs
        activities={content.activities}
        value={tabValue}
        onChange={handleTabChange}
      />

      <ActionMenu
        anchorEl={menuAnchor}
        onClose={handleMenuClose}
        onEdit={handleEdit}
        onPrint={handlePrint}
        onDelete={handleDeleteClick}
        isFromDraftTab={isFromDraftTab}
        isFromPublishedTab={isFromPublishedTab}
        isFromApprovedTab={isFromApprovedTab}
        isFromReleasedTab={isFromReleasedTab}
        isFromArchivedTab={isFromArchivedTab}
        onPublish={isFromDraftTab ? publishContent : undefined}
        onApprove={isFromPublishedTab ? approveContent : undefined}
        onRelease={isFromApprovedTab ? releaseContent : undefined}
        onArchive={!isFromArchivedTab ? archiveContent : undefined}
      />

      <DeleteDialog
        open={deleteDialogOpen}
        title="Delete Reading Practice Content"
        message={"Are you sure you want to delete {itemName}? This action cannot be undone."}
        itemName={content.cover.title}
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        confirmText="Delete"
        destructive={true}
      />
    </Box>
  );
};

export default ReadingPracticeDetails;